import { X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopilotChat, type CopilotChatSuggestion } from "@copilotkit/react-ui";
import { useEffect, useRef } from "react";
import "./AskEmmaChat.css";

interface AskEmmaChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const defaultSuggestions: CopilotChatSuggestion[] = [
  {
    title: "Who are my most valuable customers?",
    message: "Who are my most valuable customers?"
  },
  {
    title: "Show me sales data for the last 30 days",
    message: "Show me sales data for the last 30 days"
  },
  {
    title: "How can I create an SMS campaign?",
    message: "How can I create an SMS campaign?"
  },
  {
    title: "What products are selling best?",
    message: "What products are selling best?"
  }
];

// Manual suggestions component that renders outside CopilotKit
function ManualSuggestions({
  onSuggestionClick
}: {
  onSuggestionClick: (message: string) => void;
}) {
  return (
    <div className="suggestions flex flex-col gap-2 p-4 bg-white border-b">
      <div className="flex flex-wrap gap-2">
        {defaultSuggestions.map((suggestion, index) => (
          <button
            key={`manual-suggestion-${index}`}
            onClick={() => onSuggestionClick(suggestion.message)}
            className="rounded-md border border-gray-500 bg-white px-3 py-2 text-sm shadow-md cursor-pointer hover:bg-gray-50 transition-colors text-left"
          >
            {suggestion.title}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AskEmmaChat({ isOpen, onClose }: AskEmmaChatProps) {
  const chatWrapperRef = useRef<HTMLDivElement>(null);

  const handleSuggestionClick = (message: string) => {
    // Wait for CopilotKit to be fully mounted
    setTimeout(() => {
      const textarea = chatWrapperRef.current?.querySelector(
        ".copilotKitInput textarea"
      ) as HTMLTextAreaElement;

      if (!textarea) {
        console.warn("CopilotKit textarea not found");
        return;
      }

      // Focus the textarea
      textarea.focus();

      // Get the native value setter to properly update React state
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value"
      )?.set;

      // Set the value using native setter to trigger React's onChange
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(textarea, message);
      } else {
        textarea.value = message;
      }

      // Try to get React's event handler from the textarea
      const reactFiber =
        (textarea as any)._reactInternalFiber ||
        (textarea as any).__reactFiber ||
        (textarea as any).__reactInternalInstance;

      // Try to trigger onChange if we can find it
      if (reactFiber) {
        const props = reactFiber.memoizedProps || reactFiber.pendingProps;
        if (props?.onChange) {
          const syntheticEvent = {
            target: textarea,
            currentTarget: textarea,
            bubbles: true,
            cancelable: true,
            defaultPrevented: false,
            eventPhase: 2,
            isTrusted: true,
            nativeEvent: new window.Event("input"),
            preventDefault: () => {},
            stopPropagation: () => {},
            type: "change"
          };
          props.onChange(syntheticEvent);
        }
      }

      // Dispatch native events
      const inputEvent = new window.Event("input", {
        bubbles: true,
        cancelable: true
      });
      const changeEvent = new window.Event("change", {
        bubbles: true,
        cancelable: true
      });
      textarea.dispatchEvent(inputEvent);
      textarea.dispatchEvent(changeEvent);

      // Wait for React to process the state update, then send
      setTimeout(() => {
        // Try to find and click the send button
        const sendButton = chatWrapperRef.current?.querySelector(
          ".copilotKitInputControlButton"
        ) as HTMLButtonElement;

        if (sendButton) {
          // Check if button is enabled
          if (!sendButton.disabled) {
            sendButton.click();
          } else {
            // If disabled, try Enter key
            const enterKeyEvent = new window.KeyboardEvent("keydown", {
              key: "Enter",
              code: "Enter",
              keyCode: 13,
              which: 13,
              bubbles: true,
              cancelable: true,
              shiftKey: false
            });
            textarea.dispatchEvent(enterKeyEvent);
          }
        } else {
          // Fallback: try Enter key
          const enterKeyEvent = new window.KeyboardEvent("keydown", {
            key: "Enter",
            code: "Enter",
            keyCode: 13,
            which: 13,
            bubbles: true,
            cancelable: true,
            shiftKey: false
          });
          textarea.dispatchEvent(enterKeyEvent);
        }
      }, 200);
    }, 100);
  };

  useEffect(() => {
    if (isOpen) {
      const updateBackground = () => {
        // Find the chat element within this component
        const chatElement = document.querySelector(
          ".emma-chat-wrapper .copilotKitChat"
        ) as HTMLElement;
        if (chatElement) {
          chatElement.style.backgroundColor = "#ffffff";
          chatElement.style.background = "#ffffff";
          chatElement.style.setProperty(
            "--copilot-kit-background-color",
            "#ffffff",
            "important"
          );
        }
      };

      // Use MutationObserver to catch when the element is added to DOM
      const observer = new window.MutationObserver(() => {
        updateBackground();
      });

      const wrapper = document.querySelector(".emma-chat-wrapper");
      if (wrapper) {
        observer.observe(wrapper, {
          childList: true,
          subtree: true
        });
      }

      // Try immediately and with a delay
      updateBackground();
      const timeout = setTimeout(updateBackground, 100);
      const timeout2 = setTimeout(updateBackground, 500);

      return () => {
        observer.disconnect();
        clearTimeout(timeout);
        clearTimeout(timeout2);
      };
    }
  }, [isOpen]);

  return (
    <div
      className={`fixed right-0 top-0 h-full w-0 bg-white border-l shadow-lg transition-all duration-300 ease-in-out flex flex-col z-50 ${
        isOpen ? "w-96" : ""
      }`}
    >
      {/* Header */}
      <div className="h-20 flex items-center justify-between px-6 border-b shrink-0 bg-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Emma Avatar */}
          <div className="relative flex-shrink-0">
            <img
              src="/emma.png"
              alt="Emma"
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shadow-sm"
            />
          </div>

          {/* Title Section */}
          <div className="flex flex-col min-w-0">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
              Chat with Emma
              <Sparkles className="h-4 w-4 text-teal-500 fill-teal-500" />
            </h2>
            <p className="text-xs text-gray-500">
              Your friendly business assistant
            </p>
          </div>
        </div>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="rounded-full flex-shrink-0 h-8 w-8"
          aria-label="Close chat"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable Content Area - Everything scrolls together */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white">
        {/* Initial Message */}
        <div className="p-4 space-y-4 bg-white">
          {/* Emma's Initial Message */}
          <div className="flex items-start gap-2.5">
            <img
              src="/emma.png"
              alt="Emma"
              className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-200"
            />
            <div className="bg-gray-100 rounded-lg rounded-tl-sm px-4 py-3 max-w-[85%]">
              <p className="text-sm text-gray-800 leading-relaxed">
                Hi there! 👋 I'm Emma, your business buddy. Ask me anything
                about growing your business, improving campaigns, or
                understanding your customers better!
              </p>
            </div>
          </div>
        </div>

        {/* Manual Suggestions - Always visible */}
        <ManualSuggestions onSuggestionClick={handleSuggestionClick} />

        {/* Chat Content */}
        <div className="emma-chat-wrapper" ref={chatWrapperRef}>
          <CopilotChat
            instructions={`You are Emma, a friendly and helpful business assistant. Your role is to help users understand their business data, manage customers, create campaigns, and make data-driven decisions.

IMPORTANT GUIDELINES:
- Always be friendly, professional, and conversational
- When users ask about their data (customers, sales, products), ALWAYS use the available actions to fetch real data
- NEVER say "I'm having difficulty understanding" without first trying to use the available actions
- Always explain what actions you're taking when fetching data
- Provide clear, actionable answers based on the data you retrieve

AVAILABLE ACTIONS AND WHEN TO USE THEM:

1. **getCustomerAnalytics** - Use this for questions about customers:
   - "Who are my most valuable customers?" 
   - "Show me top customers"
   - "Customer insights" or "customer analytics"
   - Any question about customer value, behavior, or segmentation

2. **getSalesAnalytics** - Use this for questions about sales/revenue:
   - "Show me sales data for the last 30 days"
   - "What's my revenue this month?"
   - "Sales trends" or "sales performance"
   - Any question about sales, revenue, or financial data

3. **getProductAnalytics** - Use this for questions about products:
   - "What products are selling best?"
   - "Top selling products"
   - "Product performance" or "product insights"
   - Any question about product sales or performance

4. **fetchDetailsForCustomer** - Use this to search for specific customers by name, email, or phone

5. **listAvailableSegments** - Use this to get a list of customer segments

6. **sendSMSCampaignToACustomer** or **sendSMSCampaignToASegment** - Use these to create and send SMS campaigns

When users ask questions like the default suggestions:
- "Who are my most valuable customers?" → Use **getCustomerAnalytics** action with query "most valuable customers" or "top customers by revenue"
- "Show me sales data for the last 30 days" → Use **getSalesAnalytics** action with query "sales data for the last 30 days"
- "What products are selling best?" → Use **getProductAnalytics** action with query "best selling products"
- "How can I create an SMS campaign?" → Explain the process and offer to help create one using the campaign actions

REMEMBER: Always try to use actions to fetch real data before saying you don't understand. The analytics actions use n8n to provide accurate, real-time data from the user's business.`}
            labels={{
              placeholder: "Ask anything about your business..."
            }}
          />
        </div>
      </div>
    </div>
  );
}
